import React, { use } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cn from 'clsx';
import styles from './styles.module.scss';
import Badge from '@components-clean/core/Badge';
import Button from '@components-clean/core/button';
import Toggle from '@components-clean/core/toggle';
import { ToolbarComponents, usePageToolbar } from '@contexts-clean/page-toolbar-context';
import { ComponentNames, useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { LocalContextPersistedKey } from '@contexts/local-state-context';
import { UserContext } from '@contexts/user-context';

export function IsPopOut() {
    return sessionStorage.getItem(LocalContextPersistedKey.LockedToOrgKey) !== null;
}

export function PopOutPage(lockedToOrgKey: string, route?: string | null, allowEdit?: boolean) {
    const url = route ?? window.location.pathname;
    if (allowEdit) sessionStorage.setItem(LocalContextPersistedKey.AllowEdit, String(allowEdit));

    sessionStorage.setItem(LocalContextPersistedKey.LockedToOrgKey, lockedToOrgKey);

    window.open(url, '_blank');

    sessionStorage.removeItem(LocalContextPersistedKey.AllowEdit);
    sessionStorage.removeItem(LocalContextPersistedKey.LockedToOrgKey);
}

export const PageToolbar = () => {
    const { state, dispatch, legendToggle$, legendToggleValue } = usePageToolbar();
    const { componentHasChanges, confirmDiscardChanges } = useUnsavedChangesContext();
    const { user } = use(UserContext);
    const hasComponent = (toolbarComponent: ToolbarComponents) => state.components.includes(toolbarComponent);

    const toolbarBadges = state.toolbarBadges ?? [];

    if (!state.components.length && toolbarBadges.length === 0) return null;

    return (
        <div className={cn('k-d-flex', styles.toolbar)}>
            {toolbarBadges.length > 0 && (
                <div className={styles.toolbarBadges}>
                    {toolbarBadges.map(({ key, ...badgeProps }) => (
                        <Badge key={key} {...badgeProps} />
                    ))}
                </div>
            )}

            {hasComponent('riskReviewActions') && state.riskReviewActions && (
                <div style={{ margin: 'auto' }}>{state.riskReviewActions.content}</div>
            )}

            {hasComponent('legendToggle') && (
                <Toggle
                    data-testid="show-chart-legends"
                    defaultChecked={legendToggleValue?.current}
                    onChange={(e) => {
                        legendToggle$.next(e.value);
                        if (legendToggleValue != null) legendToggleValue.current = e.value;
                    }}
                >
                    Display Chart Legends
                </Toggle>
            )}
            {hasComponent('subjectMonitoringProfileOverviewToggle') && (
                <Toggle
                    data-testid="show-sm-profile-overview"
                    checked={state.subjectMonitoringProfileOverviewToggle ?? true}
                    onChange={(e) => {
                        dispatch({
                            type: 'setSubjectMonitoringProfileOverviewToggle',
                            subjectMonitoringProfileOverviewToggle: e.value,
                        });
                    }}
                >
                    Display Overview
                </Toggle>
            )}
            {hasComponent('commentsToggle') && (
                <Toggle
                    data-testid="show-comments"
                    checked={state.commentsToggle ?? false}
                    onChange={async (e) => {
                        if (componentHasChanges(ComponentNames.commentsSideBar)) {
                            if (!(await confirmDiscardChanges())) {
                                return;
                            }
                        }

                        dispatch({
                            type: 'setCommentsToggle',
                            commentsToggle: e.value,
                        });
                    }}
                >
                    Display Comments
                </Toggle>
            )}

            {hasComponent('popOut') && !IsPopOut() && (
                <button
                    aria-label="Pop Out Page"
                    data-test-id="pop-out-button"
                    type="button"
                    className={styles.popOutPage}
                    onClick={() => PopOutPage(user?.organisationKey as string, state.popoutRoute)}
                >
                    <FontAwesomeIcon icon={['far', 'arrow-up-right-from-square']} />
                </button>
            )}

            {hasComponent('actionButton') && state.actionButton && (
                <div style={{ margin: 'auto' }}>
                    <Button
                        primary
                        aria-label={state.actionButton?.text}
                        data-testid="action-button"
                        type="button"
                        onClick={state.actionButton.onClick}
                    >
                        {state.actionButton?.text}
                    </Button>
                </div>
            )}
        </div>
    );
};
